/**
 * MarkdownRenderer — Rich Markdown Rendering for Chat
 *
 * Central rendering engine for all AI chat messages.
 * Supports: GFM tables, KaTeX math, Mermaid diagrams, highlight.js code blocks,
 * GitHub-style admonitions, inline citations, thinking blocks.
 *
 * Visual design: borderless, tight prose, neutral colors, larger radius.
 * Inspired by ChatGPT, Claude.ai, Open WebUI rendering patterns.
 */

import { useMemo, useRef, useEffect, useState, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import hljs from 'highlight.js';
import 'katex/dist/katex.min.css';
import 'highlight.js/styles/github-dark.min.css';
import { Copy, CheckCheck, ChevronDown, ChevronRight, Download } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { sanitizeSvg } from '../../lib/sanitize';
import { ThinkingBlock, parseThinkingBlocks, stripThinkingTags } from './ThinkingBlock';
import { copyTextToClipboard, copyResultToMessage } from './-table-copy-helpers';
import { CitationHoverPopover } from './citation-hover-popover';
import { CITATION_TIERS, tierOf } from '../../lib/citation-tier';
import { resolveMarkerToCitationIndex, type MarkerCitation } from '../../lib/citation-marker';
import { TOKENS } from '../../lib/design-tokens';
import { useTheme } from '../theme-provider';

interface MarkdownRendererProps {
  content: string;
  isStreaming?: boolean;
  citations?: MarkerCitation[];
  onCitationClick?: (index: number) => void;
  /**
   * When false, inline `[N]` citation badges are NOT injected (the Citations
   * OFF toggle). Defense-in-depth alongside stripCitationMarkers so a stray
   * marker that slips the strip regex still can't render a badge — the two
   * never drift out of sync. Defaults true (badges shown).
   */
  showCitationBadges?: boolean;
}

export function MarkdownRenderer({ content, isStreaming = false, citations, onCitationClick, showCitationBadges = true }: MarkdownRendererProps) {
  // Parse thinking blocks first
  const parts = useMemo(() => parseThinkingBlocks(content), [content]);

  return (
    <div className="max-w-none text-foreground text-sm leading-[1.7]">
      {parts.map((part, i) => {
        if (part.type === 'thinking') {
          return <ThinkingBlock key={i} content={part.content} isStreaming={isStreaming && i === parts.length - 1} />;
        }
        return (
          <MarkdownContent
            key={i}
            content={part.content}
            citations={citations}
            onCitationClick={onCitationClick}
            showCitationBadges={showCitationBadges}
          />
        );
      })}
    </div>
  );
}

// ── Internal markdown renderer ──────────────────────────────────────────────

function MarkdownContent({
  content,
  citations,
  onCitationClick,
  showCitationBadges = true,
}: {
  content: string;
  citations?: MarkdownRendererProps['citations'];
  onCitationClick?: (index: number) => void;
  showCitationBadges?: boolean;
}) {
  // Gate badge injection on the Citations toggle — when OFF, no badges are
  // injected at all (in addition to the text being marker-stripped upstream).
  const hasCitations = showCitationBadges && !!citations && citations.length > 0;

  // Read the resolved theme mode so MermaidBlock receives a key-bump on
  // theme change, forcing a re-mount + re-render with the new Mermaid
  // theme. Wrapped in a try/catch so MarkdownRenderer degrades gracefully
  // when rendered outside a ThemeProvider (tests, Storybook).
  let themeMode: 'light' | 'dark' = 'dark';
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const theme = useTheme();
    themeMode = theme.mode;
  } catch {
    // No ThemeProvider in tree — keep default.
  }

  // The model emits LaTeX with bracket delimiters (\[ … \] display, \( … \)
  // inline), but remark-math only recognizes dollar delimiters ($$ … $$, $ … $),
  // so the brackets pass through as literal backslash markup. Normalize the
  // bracket form to the dollar form KaTeX understands. Done deterministically
  // here (not via a prompt rule the small model ignores). Code is protected so
  // a legitimate \[ or \( inside a code span/fence is left untouched, and only
  // CLOSED pairs are converted so a half-streamed \[ doesn't swallow the rest.
  const prepared = useMemo(() => normalizeMathDelimiters(content), [content]);

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkMath]}
      rehypePlugins={[rehypeKatex]}
      components={{
        // ── Headings — clear hierarchy ────────────────────────────
        h1({ children }) { return <h1 className="text-xl font-bold mt-6 mb-2 text-foreground">{children}</h1>; },
        h2({ children }) { return <h2 className="text-lg font-bold mt-5 mb-2 text-foreground">{children}</h2>; },
        h3({ children }) { return <h3 className="text-base font-semibold mt-4 mb-1.5 text-foreground">{children}</h3>; },
        h4({ children }) { return <h4 className="text-sm font-semibold mt-3 mb-1 text-muted-foreground">{children}</h4>; },

        // ── Paragraphs → citation badge injection ────────────────────
        p({ children }) {
          const inner = hasCitations ? injectCitationBadges(children, citations!, onCitationClick) : children;
          return <p className="my-1.5 text-sm leading-[1.7] text-foreground">{inner}</p>;
        },

        // ── Lists ──────────────────────────────────────────────────
        ul({ children }) { return <ul className="list-disc pl-5 space-y-1 my-2 text-sm text-foreground marker:text-foreground/50">{children}</ul>; },
        ol({ children }) { return <ol className="list-decimal pl-5 space-y-1 my-2 text-sm text-foreground">{children}</ol>; },
        li({ children }) {
          const inner = hasCitations ? injectCitationBadges(children, citations!, onCitationClick) : children;
          return <li className="text-sm leading-[1.7] pl-0.5 text-foreground">{inner}</li>;
        },

        // ── Text formatting ─────────────────────────────────────────
        strong({ children }) { return <strong className="font-semibold text-foreground">{children}</strong>; },
        em({ children }) { return <em className="italic">{children}</em>; },
        hr() { return <hr className="my-5 border-border" />; },

        // ── Code blocks with syntax highlighting ─────────────────────
        code({ className, children, ...props }) {
          const match = /language-(\w+)/.exec(className ?? '');
          const language = match?.[1];
          const codeStr = String(children).replace(/\n$/, '');

          // Inline code — neutral, not purple
          if (!language && !codeStr.includes('\n')) {
            return (
              <code
                className="bg-muted/80 px-1.5 py-0.5 rounded-md text-[13px] font-mono text-foreground/90"
                {...props}
              >
                {children}
              </code>
            );
          }

          // Mermaid diagrams — key includes themeMode so React unmounts +
          // remounts the block on theme change, triggering a fresh Mermaid
          // initialize() call with the correct 'dark' | 'default' theme.
          // Closes audit Risk 7.
          if (language === 'mermaid') {
            return <MermaidBlock key={`mermaid-${codeStr.slice(0, 32)}-${themeMode}`} code={codeStr} />;
          }

          // Fenced code block
          return <CodeBlock code={codeStr} language={language} />;
        },

        // ── Tables — visible structure in both modes ────────────────
        table({ children }) { return <EnhancedTable>{children}</EnhancedTable>; },
        thead({ children }) {
          return (
            <thead className="bg-muted/50 dark:bg-muted/40 border-b-2 border-border">
              {children}
            </thead>
          );
        },
        tbody({ children }) { return <tbody className="divide-y divide-border/60 dark:divide-border/40">{children}</tbody>; },
        tr({ children }) { return <tr className="hover:bg-muted/20 transition-colors">{children}</tr>; },
        th({ children }) {
          return (
            <th className="px-3 py-2.5 text-left text-xs font-bold text-foreground tracking-wide">
              {children}
            </th>
          );
        },
        td({ children }) {
          const inner = hasCitations ? injectCitationBadges(children, citations!, onCitationClick) : children;
          return <td className="px-3 py-2.5 text-sm text-foreground">{inner}</td>;
        },

        // ── Blockquotes → admonitions ────────────────────────────────
        blockquote({ children }) { return <AdmonitionBlock>{children}</AdmonitionBlock>; },

        // ── Links → internal navigation for library docs ─────────────
        a({ href, children }) {
          const isInternal = href?.startsWith('/library/') || href?.startsWith('/docs/');
          return (
            <a href={href} className="text-primary hover:underline font-medium" target={isInternal ? undefined : '_blank'} rel={isInternal ? undefined : 'noopener noreferrer'}>
              {children}
            </a>
          );
        },
      }}
    >
      {prepared}
    </ReactMarkdown>
  );
}

/**
 * Convert LaTeX bracket delimiters (\[ … \], \( … \)) — which the model emits but
 * remark-math does not parse — into the dollar delimiters KaTeX recognizes
 * ($$ … $$, $ … $). Fenced + inline code is protected so a real \[ / \( inside
 * code (regex, bash) is preserved, and only CLOSED pairs convert so a partially
 * streamed open delimiter never runs away.
 *
 * bug #20 — ALSO wrap BARE LaTeX math commands (\frac / \dfrac / \tfrac with two
 * brace args, \sqrt with optional [index] + one brace arg) the model emitted with
 * NO delimiter. remark-math only parses $…$ math, so a bare \frac{7.1}{4.8} flowed
 * out as text ("7.1 4.8") instead of a stacked fraction. We wrap such runs in
 * inline $…$ (outside code, outside existing math) so KaTeX renders them.
 */
function normalizeMathDelimiters(src: string): string {
  const hasBareCommand = /\\(?:d|t)?frac|\\sqrt/.test(src);
  if (src.indexOf('\\[') === -1 && src.indexOf('\\(') === -1 && !hasBareCommand) return src;
  const stash: string[] = [];
  let out = src
    .replace(/```[\s\S]*?```/g, (m) => `\u0000${stash.push(m) - 1}\u0000`)
    .replace(/`[^`\n]*`/g, (m) => `\u0000${stash.push(m) - 1}\u0000`);
  out = out
    .replace(/\\\[([\s\S]+?)\\\]/g, (_m, inner) => `$$${inner}$$`)
    .replace(/\\\(([\s\S]+?)\\\)/g, (_m, inner) => `$${inner}$`);
  // bug #20 — wrap BARE \\frac/\\dfrac/\\tfrac{..}{..} + \\sqrt[..]{..} runs (emitted
  // with NO delimiter) in inline math so KaTeX renders them. Split on existing
  // dollar-math spans (incl. those just created from \\[ \\] / \\( \\)) and wrap ONLY
  // the non-math TEXT segments, so a \\frac already inside $...$ is never touched
  // (no double-wrap / nested-math). Matched closing braces required.
  const bareMath =
    /\\(?:d|t)?frac\s*\{(?:[^{}]|\{[^{}]*\})*\}\s*\{(?:[^{}]|\{[^{}]*\})*\}|\\sqrt\s*(?:\[[^\]\n]*\])?\s*\{(?:[^{}]|\{[^{}]*\})*\}/g;
  out = out
    .split(/(\$\$[\s\S]+?\$\$|\$[^$\n]+?\$)/g)
    .map((seg, i) => (i % 2 === 1 ? seg : seg.replace(bareMath, (m) => `$${m}$`)))
    .join('');
  out = out.replace(/\u0000(\d+)\u0000/g, (_m, i) => stash[Number(i)] ?? '');
  return out;
}

// ── CodeBlock Component ─────────────────────────────────────────────────────

function CodeBlock({ code, language }: { code: string; language?: string }) {
  const [copied, setCopied] = useState(false);
  const [collapsed, setCollapsed] = useState(code.split('\n').length > 30);
  const codeRef = useRef<HTMLElement>(null);

  // Highlight on mount
  useEffect(() => {
    if (codeRef.current) {
      hljs.highlightElement(codeRef.current);
    }
  }, [code, language]);

  const detectedLang = language ?? hljs.highlightAuto(code).language ?? 'text';
  const lineCount = code.split('\n').length;
  const isLong = lineCount > 30;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div
      className="relative group rounded-2xl border border-border/30 dark:border-border/20 my-4 overflow-hidden"
      style={{ background: TOKENS.card }}
    >
      {/* Header bar — language + actions */}
      <div
        className="flex items-center justify-between px-4 py-2 text-xs"
        style={{ background: TOKENS.muted }}
      >
        <span
          className="font-mono text-[11px]"
          style={{ color: TOKENS.mutedFg }}
        >
          {detectedLang}
        </span>
        <div className="flex items-center gap-3">
          {isLong && (
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="flex items-center gap-1 transition-colors hover:opacity-80"
              style={{ color: TOKENS.mutedFg }}
            >
              {collapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              <span>{lineCount} lines</span>
            </button>
          )}
          <button
            onClick={handleCopy}
            className="flex items-center gap-1 transition-colors hover:opacity-80"
            style={{ color: TOKENS.mutedFg }}
          >
            {copied ? <CheckCheck className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
            <span>{copied ? 'Copied' : 'Copy'}</span>
          </button>
        </div>
      </div>
      {/* Code content */}
      <div className={cn('overflow-x-auto', collapsed && 'max-h-[200px] overflow-y-hidden')}>
        <pre className="p-4 m-0">
          <code ref={codeRef} className={language ? `language-${language}` : ''}>
            {code}
          </code>
        </pre>
      </div>
      {collapsed && (
        <button
          onClick={() => setCollapsed(false)}
          className="w-full py-2 text-xs transition-colors hover:opacity-80 bg-gradient-to-t from-card to-transparent"
          style={{ color: TOKENS.mutedFg }}
        >
          Show all {lineCount} lines
        </button>
      )}
    </div>
  );
}

// ── MermaidBlock ─────────────────────────────────────────────────────────────

/**
 * Map our ThemeProvider mode to a Mermaid theme name.
 * 'dark' → 'dark', anything else → 'default' (Mermaid's light theme).
 *
 * Closes audit Risk 7: Mermaid was previously hardcoded to `theme: 'dark'`
 * regardless of the active application theme.
 */
function mermaidThemeFromMode(mode: 'light' | 'dark'): 'dark' | 'default' {
  return mode === 'dark' ? 'dark' : 'default';
}

function MermaidBlock({ code }: { code: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  // Read the resolved theme mode so we can pass the correct Mermaid theme.
  // useTheme() is safe here — MermaidBlock is always rendered inside
  // MarkdownRenderer which lives under ThemeProvider in the app tree.
  // On theme change the `mode` value changes, the key-bump below forces
  // the parent MarkdownRenderer to remount this component, triggering a
  // fresh render call with the new theme.
  let mode: 'light' | 'dark' = 'dark';
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const theme = useTheme();
    mode = theme.mode;
  } catch {
    // ThemeProvider not in tree (tests / storybook) — fall back to dark.
  }

  const mermaidTheme = mermaidThemeFromMode(mode);

  useEffect(() => {
    let cancelled = false;
    import('mermaid').then(async (mermaid) => {
      if (cancelled) return;
      try {
        mermaid.default.initialize({
          startOnLoad: false,
          theme: mermaidTheme,
          securityLevel: 'strict',
        });
        const { svg: rendered } = await mermaid.default.render(`mermaid-${Date.now()}`, code);
        if (!cancelled) setSvg(rendered);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to render diagram');
      }
    });
    return () => { cancelled = true; };
  // Re-run when the theme changes so the diagram is re-initialized with
  // the new Mermaid theme.  The parent MarkdownContent key-bumps this
  // component on theme change (see the `key` prop below in MarkdownContent).
  }, [code, mermaidTheme]);

  if (error) {
    return <CodeBlock code={code} language="mermaid" />;
  }

  return (
    <div
      ref={containerRef}
      className="my-4 p-4 rounded-2xl border border-border/30 bg-card overflow-x-auto"
      dangerouslySetInnerHTML={{ __html: sanitizeSvg(svg) }}
    />
  );
}

// ── EnhancedTable ───────────────────────────────────────────────────────────

function EnhancedTable({ children }: { children: ReactNode }) {
  const tableRef = useRef<HTMLTableElement>(null);
  const [copied, setCopied] = useState(false);

  const handleCopyTable = async () => {
    if (!tableRef.current) return;
    // #674 — previously this swallowed any clipboard failure (insecure
    // context, permission denied, unsupported API), leaving the user
    // with no feedback. Now route every outcome through a toast so the
    // result is visible even after the hover-only button has faded
    // back out.
    const result = await copyTextToClipboard(tableRef.current.innerText);
    const msg = copyResultToMessage(result);
    if (msg.tone === 'success') {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success(msg.text);
    } else {
      toast.error(msg.text);
    }
  };

  const handleExportCSV = () => {
    if (!tableRef.current) return;
    const rows = tableRef.current.querySelectorAll('tr');
    const csv = Array.from(rows).map(row => {
      const cells = row.querySelectorAll('th, td');
      return Array.from(cells).map(c => `"${c.textContent?.replace(/"/g, '""') ?? ''}"`).join(',');
    }).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'table-export.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="my-4 relative group">
      <div className="absolute right-2 top-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
        <button onClick={handleCopyTable} className="p-1.5 rounded-lg bg-card border border-border/40 hover:bg-muted text-xs flex items-center gap-1 transition-colors" aria-label="Copy table" title="Copy table">
          {copied ? <CheckCheck className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
        </button>
        <button onClick={handleExportCSV} className="p-1.5 rounded-lg bg-card border border-border/40 hover:bg-muted text-xs flex items-center gap-1 transition-colors" aria-label="Export as CSV" title="Export CSV">
          <Download className="h-3 w-3" />
        </button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table ref={tableRef} className="w-full text-sm">
          {children}
        </table>
      </div>
    </div>
  );
}

// ── Admonition Blocks ───────────────────────────────────────────────────────

const ADMONITION_PATTERNS: Record<string, { border: string; bg: string; icon: string; label: string }> = {
  NOTE:      { border: 'border-blue-500/50',   bg: 'bg-blue-500/5 dark:bg-blue-500/10',     icon: 'ℹ️',  label: 'Note' },
  TIP:       { border: 'border-green-500/50',  bg: 'bg-green-500/5 dark:bg-green-500/10',    icon: '💡', label: 'Tip' },
  WARNING:   { border: 'border-yellow-500/50', bg: 'bg-yellow-500/5 dark:bg-yellow-500/10',  icon: '⚠️', label: 'Warning' },
  CAUTION:   { border: 'border-red-500/50',    bg: 'bg-red-500/5 dark:bg-red-500/10',        icon: '🔴', label: 'Caution' },
  IMPORTANT: { border: 'border-purple-500/50', bg: 'bg-purple-500/5 dark:bg-purple-500/10',  icon: '⭐', label: 'Important' },
};

function AdmonitionBlock({ children }: { children: ReactNode }) {
  // Check if first text child starts with [!TYPE]
  const childArray = Array.isArray(children) ? children : [children];
  const firstChild = childArray[0];
  let textContent = '';

  if (firstChild && typeof firstChild === 'object' && 'props' in firstChild) {
    const p = firstChild as { props?: { children?: ReactNode } };
    if (p.props?.children) {
      const innerChildren = Array.isArray(p.props.children) ? p.props.children : [p.props.children];
      textContent = innerChildren.map(c => (typeof c === 'string' ? c : '')).join('');
    }
  }

  const admonitionMatch = textContent.match(/^\[!(NOTE|TIP|WARNING|CAUTION|IMPORTANT)\]\s*/);
  if (admonitionMatch) {
    const type = admonitionMatch[1]!;
    const config = ADMONITION_PATTERNS[type]!;

    return (
      <div className={cn('my-4 rounded-xl border-l-[3px] p-4', config.border, config.bg)}>
        <div className="flex items-center gap-2 font-semibold text-sm mb-1">
          <span>{config.icon}</span>
          <span>{config.label}</span>
        </div>
        <div className="text-sm [&>p:first-child]:mt-0">
          {childArray.map((child, i) => {
            if (i === 0 && typeof child === 'object' && 'props' in child) {
              const p = child as { props?: { children?: ReactNode } };
              if (p.props?.children) {
                const innerChildren = Array.isArray(p.props.children) ? p.props.children : [p.props.children];
                const cleaned = innerChildren.map(c => {
                  if (typeof c === 'string') return c.replace(/^\[!(NOTE|TIP|WARNING|CAUTION|IMPORTANT)\]\s*/, '');
                  return c;
                });
                return <p key={i}>{cleaned}</p>;
              }
            }
            return child;
          })}
        </div>
      </div>
    );
  }

  // Regular blockquote
  return (
    <blockquote className="border-l-[3px] border-border pl-4 text-foreground/80 my-4 italic">
      {children}
    </blockquote>
  );
}

// ── Citation Badge Injection ────────────────────────────────────────────────

function injectCitationBadges(
  children: ReactNode,
  citations: NonNullable<MarkdownRendererProps['citations']>,
  onCitationClick?: (index: number) => void,
): ReactNode {
  if (!children) return children;

  const processNode = (node: ReactNode): ReactNode => {
    if (typeof node === 'string') {
      // Match THREE marker forms so live inline references always become badges:
      //   1. numbered  [Source 1] / [1] / 【Source 1】 / 【1】 (library + persisted)
      //   2. tool-name 【ext.acme.get_weather】 / [iq.cmms.listWorkOrders] — what a
      //      CJK model emits for an MCP/tool result it was never told to number.
      //      Mapped to the matching live (MCP) citation by docName so it renders
      //      as a numbered, tier-colored badge instead of literal text.
      const parts = node.split(/([[【](?:Source\s*)?\d+[\]】]|[[【][^[\]【】]*(?:ext|iq|forge)\.[a-zA-Z][a-zA-Z0-9_.]*[^[\]【】]*[\]】]|[[【]Web\s*\d+[\]】])/gi);
      if (parts.length === 1) return node;

      // A marker-SHAPED token that doesn't resolve to a real citation
      // ([Source 5] with 3 sources, [Web 4] with no 4th web source, a tool
      // marker with no matching MCP citation) is a DANGLING reference — strip
      // it so the prose never shows a raw "[Source 5]" / "[Web 4]" (bug #2/#12).
      const isMarkerShape = (s: string): boolean =>
        /^[[【](?:Source\s*)?\d+[\]】]$/i.test(s) ||
        /^[[【]Web\s*\d+[\]】]$/i.test(s) ||
        /^[[【][^[\]【】]*(?:ext|iq|forge)\.[a-zA-Z][a-zA-Z0-9_.]*[^[\]【】]*[\]】]$/i.test(s);

      return parts.map((part, i) => {
        const idx = resolveMarkerToCitationIndex(part, citations);
        if (idx !== null) {
          const citation = citations[idx];
          if (!citation) return isMarkerShape(part) ? null : part;

          // Wave 2D: wrap with CitationHoverPopover for hover preview.
          // Click still propagates to CitationModal via onCitationClick.
          // source-attribution: colour the inline [N] marker by its source tier
          // (blue Library / amber AI / teal Web) so the verified-vs-unverified
          // trust signal carries in the prose, not only the side panel.
          const tierBadge = CITATION_TIERS[tierOf(citation)].inlineBadge;
          const badge = (
            <button
              key={i}
              onClick={() => onCitationClick?.(idx)}
              className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold mx-0.5 align-super cursor-pointer transition-colors ${tierBadge}`}
              title={`${citation.docName}${citation.page ? `, p.${citation.page}` : ''}`}
            >
              {idx + 1}
            </button>
          );
          return (
            <CitationHoverPopover key={i} citation={citation}>
              {badge}
            </CitationHoverPopover>
          );
        }
        // Unresolved but marker-shaped → strip (dangling reference). Plain
        // text → keep as-is.
        return isMarkerShape(part) ? null : part;
      });
    }

    if (Array.isArray(node)) {
      return node.map((child, i) => <span key={i}>{processNode(child)}</span>);
    }

    return node;
  };

  if (Array.isArray(children)) {
    return children.map((child, i) => <span key={i}>{processNode(child)}</span>);
  }
  return processNode(children);
}
